<template>
  <SkeletonLoader>
    <div class="atp2-sk">
      <!-- Filter cards: type/code · providers/presenter · priorities -->
      <div class="atp2-sk-filters">
        <div v-for="card in filterCards" :key="card.id" class="atp2-sk-card">
          <div class="sk-line sk-line--sm" :style="{ width: card.labelWidth }" />
          <div class="atp2-sk-inputs">
            <div v-for="w in card.inputs" :key="w" class="sk-rect" :style="{ width: w, height: '40px' }" />
          </div>
        </div>
      </div>

      <!-- Timeframe chips -->
      <div class="atp2-sk-chips">
        <div v-for="w in chipWidths" :key="w" class="sk-rect" :style="{ width: w, height: '32px' }" />
      </div>

      <!-- KPI strip: four tiles + the acceptance funnel -->
      <div class="atp2-sk-strip">
        <div v-for="t in 4" :key="`tile-${t}`" class="atp2-sk-tile">
          <div class="sk-line sk-line--sm" style="width: 78px" />
          <div class="sk-line sk-line--xl" style="width: 128px" />
        </div>
        <div class="atp2-sk-funnel">
          <div class="sk-line sk-line--xl" style="width: 96px" />
          <div class="sk-rect" style="width: 100%; height: 12px" />
        </div>
      </div>

      <!-- Provider table -->
      <div class="atp2-sk-table">
        <div class="atp2-sk-row atp2-sk-row--head">
          <div v-for="c in 7" :key="`h-${c}`" class="sk-line sk-line--sm" style="width: 70%" />
        </div>
        <div v-for="r in 6" :key="`r-${r}`" class="atp2-sk-row">
          <div v-for="c in 7" :key="`c-${r}-${c}`" class="sk-line" style="width: 72%" />
        </div>
      </div>

      <!-- Three donuts -->
      <div class="atp2-sk-donuts">
        <div v-for="d in 3" :key="`d-${d}`" class="atp2-sk-donut">
          <div class="sk-circle" />
          <div class="atp2-sk-legend">
            <div v-for="l in 5" :key="`l-${d}-${l}`" class="sk-line" style="width: 100%" />
          </div>
        </div>
      </div>
    </div>
  </SkeletonLoader>
</template>

<script>
import SkeletonLoader from '@/components/SkeletonLoader.vue';

/**
 * Loading state for the V2 panel. Mirrors the real layout's rhythm — filters,
 * timeframe chips, the four-tile KPI strip, the provider table, the three donuts —
 * so the page does not visibly reflow when the data lands.
 *
 * V1's AdvancedTxPlannedSkeleton.vue is a different shape and is left untouched.
 */
export default {
  name: 'AdvancedTxPlannedV2Skeleton',
  components: { SkeletonLoader },
  data() {
    return {
      filterCards: [
        { id: 1, labelWidth: '120px', inputs: ['46%', '46%'] },
        { id: 2, labelWidth: '92px', inputs: ['60%', '34%'] },
        { id: 3, labelWidth: '150px', inputs: ['100%'] }
      ],
      chipWidths: ['84px', '92px', '96px', '110px', '86px', '78px']
    };
  }
};
</script>

<style lang="scss" scoped>
.atp2-sk { padding: 20px 24px 32px; }

.atp2-sk-filters {
  display: grid;
  grid-template-columns: 1.05fr 1.25fr .95fr;
  gap: 14px;
}

.atp2-sk-card {
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 14px;
  padding: 15px 16px;
}

.atp2-sk-inputs { display: flex; gap: 12px; margin-top: 8px; }

.atp2-sk-chips { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 14px; }

.atp2-sk-strip {
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;
  gap: 24px;
  margin-top: 20px;
  padding: 16px 0;
  border-top: 1px solid #dde4ec;
  border-bottom: 1px solid #dde4ec;
}

.atp2-sk-tile { display: flex; flex-direction: column; gap: 8px; }

.atp2-sk-funnel {
  flex: 1;
  min-width: 260px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
}

.atp2-sk-table {
  margin-top: 22px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 16px;
  overflow: hidden;
}

.atp2-sk-row {
  display: grid;
  grid-template-columns: minmax(150px, 1.5fr) repeat(6, 1fr);
  gap: 10px;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid #dde4ec;

  &:last-child { border-bottom: 0; }
  &--head { background: #fafcfe; }
}

.atp2-sk-donuts {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-top: 16px;
  background: #fff;
  border: 1px solid #dde4ec;
  border-radius: 16px;
  overflow: hidden;
}

.atp2-sk-donut {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 20px;
  border-right: 1px solid #dde4ec;

  &:last-child { border-right: 0; }
}

// Size only — SkeletonLoader's global .sk-circle supplies the shimmer gradient,
// and setting `background` here would clobber its background-image.
.sk-circle {
  flex: none;
  width: 126px;
  height: 126px;
}

.atp2-sk-legend { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px; }

@media (max-width: 1120px) {
  .atp2-sk-filters { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .atp2-sk-donuts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 760px) {
  .atp2-sk { padding: 14px 16px 24px; }
  .atp2-sk-filters,
  .atp2-sk-donuts { grid-template-columns: minmax(0, 1fr); }
  .atp2-sk-donut { border-right: 0; border-bottom: 1px solid #dde4ec; }
  .atp2-sk-row { grid-template-columns: repeat(3, 1fr); }
}
</style>
